import React from 'react';
import { fmtMoney, fmtPct, fmtDelta, approxEq } from '../../utils/priceMath';

// Quote page "Assembly Pricing" card (F20 / F02 / F10 / F39 / F40).
//
// One block per assembly on the quote. Every figure is PER SET unless it says
// "× N sets"; the table splits the set into Manufactured parts / Purchased
// components / Assembly operations so the operator can see which bucket moves
// when a line changes, and both the QUOTED per-set price (Σ line price × qty ÷
// sets — what the Subtotal bills) and the CALCULATED per-set price (Σ
// calculated_price × qty ÷ sets — what the breakdowns say) are shown with a
// drift badge when they disagree.
//
// The override box is NEVER pre-filled: an override exists only when the
// operator types one, and then the bold total says so.

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

const systemPriceText = (s) => {
  if (!s.systemPrice) return { text: 'not set', title: 'assemblies.assembly_price is empty — written on quote approval or by hand on the Assemblies page' };
  if (s.systemPriceSource === 'quote_approval') {
    return {
      text: `from Quote #${s.systemPriceQuoteId || '?'}${s.systemPriceSetAt ? ` approved ${fmtDate(s.systemPriceSetAt)}` : ''}`,
      title: 'Written by approve_quote at that quote\'s rates. Not a live figure.',
    };
  }
  if (s.systemPriceSource === 'manual') {
    return { text: `manual${s.systemPriceSetAt ? ` (${fmtDate(s.systemPriceSetAt)})` : ''}`, title: 'Typed on the Assemblies page' };
  }
  return { text: 'source not recorded', title: 'Set before price provenance was recorded' };
};

const setCountNote = (s) => {
  if (s.setCountSource === 'primary') return `sets = primary line qty ÷ ppq ${s.ppq}`;
  if (s.setCountSource === 'ops') return 'sets = ASSEMBLY OPS line qty (no primary line on the quote)';
  return 'no primary or ops line found — 1 set assumed; Qty cascade is disabled';
};

/**
 * props
 *   summaries          array from summarizeAssemblyGroup (one per assembly)
 *   overrides          assemblyPriceOverrides state
 *   isEditingAllowed
 *   onOverrideChange   (asmId, rawValue | '') => void
 *   onSetCountCommit   (asmId, n) => void
 *   SetCountInput      component ({currentSetCount, disabled, onCommit})
 */
export default function QuoteAssemblyPricingCard({ summaries, overrides, isEditingAllowed, onOverrideChange, onSetCountCommit, SetCountInput }) {
  if (!summaries || summaries.length === 0) return null;
  return (
    <div className="card mt-3 shadow-sm border-info">
      <div className="card-header bg-info bg-opacity-10 py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h6 className="mb-0 fw-bold">Assembly Pricing <span className="text-muted small fw-normal">— per set, then × sets</span></h6>
        <span className="small text-muted">Quoted = Σ line price × qty ÷ sets (what the Subtotal bills) · Calculated = Σ breakdown calculated price × qty ÷ sets</span>
      </div>
      <div className="card-body py-2">
        {summaries.map(s => {
          const raw = overrides ? overrides[s.asmId] : undefined;
          const overrideRaw = raw === undefined || raw === null ? '' : String(raw);
          const sys = systemPriceText(s);
          const driftShown = s.rows.some(r => r.calcKnown > 0) && !approxEq(s.total.price, s.total.calc);
          return (
            <div key={s.asmId} className="border rounded p-2 mb-2" style={{ fontSize: '0.85rem' }}>
              <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="fw-bold">{s.name}</span>
                  {s.retired && <span className="badge bg-warning text-dark" title="This assembly is inactive (retired) in the catalog. Its definition is still used for set count and system price.">retired assembly</span>}
                  {s.unknownAssembly && <span className="badge bg-danger" title="The assembly definition could not be loaded, so ppq and system price are unknown.">assembly not found</span>}
                  <span className="badge bg-light text-dark border">{s.counts.parts} part{s.counts.parts === 1 ? '' : 's'} · {s.counts.components} component{s.counts.components === 1 ? '' : 's'}{s.counts.ops ? ' · ops' : ''}</span>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <label className="small text-muted mb-0" title={setCountNote(s)}>Qty of Assemblies:</label>
                  <SetCountInput currentSetCount={s.setCount} disabled={!isEditingAllowed || s.setCountSource === 'fallback'}
                                 onCommit={(n) => onSetCountCommit(s.asmId, n)} />
                  <span className="text-muted small" title={setCountNote(s)}>set{s.setCount !== 1 ? 's' : ''}{s.setCountSource !== 'primary' && ' *'}</span>
                  <span className="fw-bold fs-6 ms-2" title={s.hasOverride ? `override ${fmtMoney(s.override)} × ${s.setCount} sets` : `quoted ${fmtMoney(s.total.price)}/set × ${s.setCount} sets`}>
                    {s.hasOverride ? 'Override ' : 'Quoted '}{fmtMoney(s.effectivePerSet)} × {s.setCount} = {fmtMoney(s.effectiveTotal)}
                  </span>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-sm table-bordered mb-1 align-middle" style={{ fontSize: '0.8rem' }}>
                  <thead className="table-light">
                    <tr>
                      <th>Per set</th>
                      <th className="text-end text-muted">Cost / set</th>
                      <th className="text-end">Quoted / set</th>
                      <th className="text-end">Calculated / set</th>
                      <th className="text-end">Margin (quoted)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.rows.map(r => (
                      <tr key={r.key} className={r.lines === 0 ? 'text-muted' : ''}>
                        <td>{r.label} <span className="text-muted small">({r.lines} line{r.lines === 1 ? '' : 's'})</span></td>
                        <td className="text-end text-muted" title={r.costKnown < r.lines ? `${r.lines - r.costKnown} line(s) have no cost breakdown` : 'Σ breakdown total_cost × qty ÷ sets'}>
                          {r.lines === 0 ? '—' : fmtMoney(r.cost)}{r.costKnown < r.lines && r.lines > 0 && <span className="badge bg-warning text-dark ms-1">incomplete</span>}
                        </td>
                        <td className="text-end" title="Σ line price × qty ÷ sets">{r.lines === 0 ? '—' : fmtMoney(r.price)}</td>
                        <td className="text-end" title="Σ breakdown calculated price × qty ÷ sets">{r.lines === 0 || r.calcKnown === 0 ? '—' : fmtMoney(r.calc)}</td>
                        <td className="text-end">{fmtPct(r.margin)}</td>
                      </tr>
                    ))}
                    <tr className="table-light fw-bold">
                      <td>Total per set</td>
                      <td className="text-end text-muted" title="Σ bucket costs">{s.costComplete ? fmtMoney(s.total.cost) : <span title={`no cost on: ${s.missingCost.join(', ')}`}>{fmtMoney(s.total.cost)} <span className="badge bg-warning text-dark">incomplete</span></span>}</td>
                      <td className="text-end" title="This is the number the Subtotal uses (unless an override is set)">{fmtMoney(s.total.price)}{!s.hasOverride && <span className="badge bg-success ms-1" title="Used by the Subtotal">billed</span>}</td>
                      <td className="text-end">
                        {fmtMoney(s.total.calc)}
                        {driftShown && (
                          <span className="badge bg-warning text-dark ms-1" title={`quoted ${fmtMoney(s.total.price)} − calculated ${fmtMoney(s.total.calc)} = ${fmtDelta(s.drift)} per set`}>
                            drift {fmtDelta(s.drift)}
                          </span>
                        )}
                      </td>
                      <td className="text-end" title={s.total.calcMargin != null ? `calculated margin ${fmtPct(s.total.calcMargin)}` : ''}>{fmtPct(s.total.margin)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="row g-2 align-items-end">
                <div className="col">
                  <div className="text-muted small">System Price (assemblies.assembly_price)</div>
                  <div className={s.systemPrice ? 'fw-semibold' : 'text-muted fst-italic'} title={sys.title}>
                    {s.systemPrice ? fmtMoney(s.systemPrice) : 'Not set'} <span className="text-muted small fw-normal">{sys.text}</span>
                  </div>
                </div>
                <div className="col">
                  <label className="text-muted small d-block" htmlFor={`asm-override-${s.asmId}`}>Override price (per set) — optional</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">$</span>
                    <input id={`asm-override-${s.asmId}`} type="number" step="0.01" className="form-control"
                           value={overrideRaw} placeholder={`${s.total.price.toFixed(2)} (quoted)`}
                           title="Leave blank to bill the quoted line sum. Typing a value bills override × sets instead and marks every line in this group."
                           onChange={(e) => onOverrideChange(s.asmId, e.target.value)} disabled={!isEditingAllowed} />
                    {s.hasOverride && (
                      <button className="btn btn-outline-secondary" type="button" title="Clear the override and bill the quoted line sum"
                              onClick={() => onOverrideChange(s.asmId, '')} disabled={!isEditingAllowed}>Clear</button>
                    )}
                  </div>
                  {s.hasOverride && (
                    <div className="small mt-1">
                      <span className="badge bg-warning text-dark me-1">override in force</span>
                      <span className="text-muted">override − quoted = {fmtDelta(s.overrideDelta)}/set · lines in this group are billed $0.00 individually</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div className="small text-muted mt-1">
          Approving writes each on-quote assembly's per-set price to its System Price (source "quote approval"). Overrides are only ever typed here — nothing pre-fills them.
        </div>
      </div>
    </div>
  );
}
